class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]',
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('a[aria-selected="true"]')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]',
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      try {
        // Escape special regex characters in the search term
        const escapedPreviousTerm = this.escapeRegex(previousTerm);
        if (currentButtonText.match(new RegExp(escapedPreviousTerm, 'g')).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
      } catch (e) {
        // If regex fails for any reason, just proceed with replacement
        console.warn('Regex error in updateSearchForTerm:', e);
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }
  
  escapeRegex(str) {
    // Escape special regex characters: . * + ? ^ $ { } ( ) | [ ] \
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"]'),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page,article&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
        
        // After rendering, check if we need supplementary results
        this.checkAndFetchSupplementaryResults(searchTerm, queryKey);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }
  
  checkAndFetchSupplementaryResults(searchTerm, queryKey) {
    // Count visible product results after filtering
    const productResults = this.predictiveSearchResults.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item');
    const visibleProductCount = productResults.length;
    const maxProducts = 6; // Maximum products to show in dropdown (avoid excessive scrolling)
    
    console.log(`Predictive search returned ${visibleProductCount} filtered products`);
    
    // If we have more than the max, hide the excess products
    if (visibleProductCount > maxProducts) {
      console.log(`Limiting results from ${visibleProductCount} to ${maxProducts} products`);
      productResults.forEach((item, index) => {
        if (index >= maxProducts) {
          item.style.display = 'none';
        }
      });
    }
    
    // If we have less than 4 products, try to fetch more using a second query approach
    // Max total will be capped at maxProducts to avoid excessive scrolling
    const displayedCount = Math.min(visibleProductCount, maxProducts);
    if (displayedCount < 4 && displayedCount > 0) {
      console.log('Less than 4 products shown, attempting supplementary search...');
      this.fetchSupplementaryResults(searchTerm, queryKey);
    }
  }
  
  fetchSupplementaryResults(searchTerm, queryKey) {
    // Make a second predictive search call with a modified query that might return different products
    const words = searchTerm.trim().split(/\s+/);
    let supplementaryQuery;
    
    if (words.length > 1) {
      // Multi-word search: Use just the first word for broader results
      // Example: "nexstar 8se" -> "nexstar"
      supplementaryQuery = words[0];
      
      // If the first word is too short (abbreviation), skip supplementary search
      // The original search with synonyms is already optimal
      if (supplementaryQuery.length <= 3) {
        console.log(`Multi-word search with short abbreviation "${supplementaryQuery}", skipping supplementary search (synonyms already applied)`);
        return;
      }
      
      console.log(`Multi-word search, trying supplementary search with broader term: "${supplementaryQuery}"`);
    } else {
      // Single-word search
      // For very short terms (≤ 3 chars), likely abbreviations or SKUs - skip supplementary search
      // Shopify's synonym matching in the initial search is already optimal for these
      if (searchTerm.length <= 3) {
        console.log(`Search term "${searchTerm}" is very short (likely abbreviation/SKU), skipping supplementary search (synonyms already applied)`);
        return;
      }
      
      // For 4-5 character terms, keep as-is (don't truncate or it becomes too short)
      if (searchTerm.length <= 5) {
        console.log(`Search term "${searchTerm}" is short, skipping supplementary search to avoid over-broadening`);
        return;
      }
      
      // For longer terms, remove last 2-3 characters to broaden the search
      // Example: "celestron" -> "celest" or "telescope" -> "telesc"
      const charsToRemove = searchTerm.length > 7 ? 3 : 2;
      supplementaryQuery = searchTerm.substring(0, searchTerm.length - charsToRemove);
      console.log(`Single-word search, trying supplementary search with partial term: "${supplementaryQuery}"`);
    }
    
    const supplementaryQueryKey = supplementaryQuery.replace(' ', '-').toLowerCase();
    
    // Don't fetch if we've already tried this query
    if (this.cachedResults[supplementaryQueryKey]) {
      console.log('Supplementary query already cached, skipping');
      return;
    }
    
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(supplementaryQuery)}&resources[type]=product&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          console.log('Supplementary search failed, using original results');
          return null;
        }
        return response.text();
      })
      .then((text) => {
        if (!text) return;
        
        const supplementaryMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Cache the supplementary results
        this.cachedResults[supplementaryQueryKey] = supplementaryMarkup;
        
        // Merge the results
        this.mergeSupplementaryResults(supplementaryMarkup, queryKey);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        console.log('Supplementary search error:', error);
        // Keep original results on error
      });
  }
  
  mergeSupplementaryResults(supplementaryMarkup, originalQueryKey) {
    // Parse supplementary results
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = supplementaryMarkup;
    
    const supplementaryProducts = tempDiv.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item');
    if (supplementaryProducts.length === 0) {
      console.log('No supplementary products found');
      return;
    }
    
    // Get current results
    const currentProductsList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!currentProductsList) return;
    
    // Get existing product URLs and titles to avoid duplicates
    // Use normalized URLs (remove domain, query params) and titles as fallback
    const existingProducts = new Map();
    
    currentProductsList.querySelectorAll('a.predictive-search__item--link-with-thumbnail').forEach(link => {
      const url = this.normalizeProductUrl(link.getAttribute('href'));
      const title = link.querySelector('.predictive-search__item-heading')?.textContent?.trim();
      existingProducts.set(url, true);
      if (title) {
        existingProducts.set(title.toLowerCase(), true);
      }
    });
    
    console.log(`Current products: ${existingProducts.size / 2}, checking ${supplementaryProducts.length} supplementary products`);
    
    const currentCount = currentProductsList.querySelectorAll('.predictive-search__list-item').length;
    const maxProducts = 6; // Maximum products to show in dropdown (avoid excessive scrolling)
    const availableSlots = Math.max(0, maxProducts - currentCount);
    
    let addedCount = 0;
    let skippedDuplicates = 0;
    
    // Add unique products up to the limit
    supplementaryProducts.forEach((productItem) => {
      if (addedCount >= availableSlots) return;
      
      const productLink = productItem.querySelector('a.predictive-search__item--link-with-thumbnail');
      const productUrl = productLink?.getAttribute('href');
      const productTitle = productLink?.querySelector('.predictive-search__item-heading')?.textContent?.trim();
      
      if (!productUrl) return;
      
      const normalizedUrl = this.normalizeProductUrl(productUrl);
      const normalizedTitle = productTitle?.toLowerCase();
      
      // Check for duplicates by both URL and title
      const isDuplicate = existingProducts.has(normalizedUrl) || 
                         (normalizedTitle && existingProducts.has(normalizedTitle));
      
      if (!isDuplicate) {
        // Clone and append the product item
        const clonedItem = productItem.cloneNode(true);
        
        // Update the ID to avoid conflicts (append -supplementary-{count})
        const linkElement = clonedItem.querySelector('a');
        if (linkElement) {
          const currentId = linkElement.getAttribute('id');
          if (currentId) {
            linkElement.setAttribute('id', `${currentId}-supplementary-${addedCount}`);
          }
        }
        
        currentProductsList.appendChild(clonedItem);
        
        // Add to existing products map
        existingProducts.set(normalizedUrl, true);
        if (normalizedTitle) {
          existingProducts.set(normalizedTitle, true);
        }
        
        addedCount++;
      } else {
        skippedDuplicates++;
      }
    });
    
    if (addedCount > 0) {
      console.log(`Added ${addedCount} supplementary product(s) to results (skipped ${skippedDuplicates} duplicates)`);
      
      // Update the cached results with the merged version
      this.cachedResults[originalQueryKey] = this.predictiveSearchResults.innerHTML;
      
      // Update live region count
      const newTotal = currentProductsList.querySelectorAll('.predictive-search__list-item').length;
      this.setLiveRegionText(`${newTotal} products found`);
    } else {
      console.log(`No unique supplementary products to add (skipped ${skippedDuplicates} duplicates)`);
    }
  }
  
  normalizeProductUrl(url) {
    if (!url) return '';
    
    // Remove query parameters and hash
    let normalized = url.split('?')[0].split('#')[0];
    
    // Remove domain if present (handle both relative and absolute URLs)
    if (normalized.startsWith('http')) {
      try {
        const urlObj = new URL(normalized);
        normalized = urlObj.pathname;
      } catch (e) {
        // If URL parsing fails, just use the original
      }
    }
    
    // Ensure it starts with / and ends without /
    normalized = normalized.replace(/\/+$/, ''); // Remove trailing slashes
    if (!normalized.startsWith('/')) {
      normalized = '/' + normalized;
    }
    
    return normalized.toLowerCase();
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    
    // Make results focusable with Tab key
    this.makeResultsFocusable();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  makeResultsFocusable() {
    // Add tabindex to all result items (links and buttons)
    const items = this.querySelectorAll('[role="option"]');
    items.forEach(item => {
      item.setAttribute('tabindex', '0');
    });
  }

  makeResultsUnfocusable() {
    // Remove from tab order when dropdown closes
    const items = this.querySelectorAll('[role="option"]');
    items.forEach(item => {
      item.setAttribute('tabindex', '-1');
    });
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    
    // Remove results from tab order
    this.makeResultsUnfocusable();
  }
}

customElements.define('predictive-search', PredictiveSearch);
